import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { ConfigProvider } from 'antd';
import ptBR from 'antd/lib/locale/pt_BR';
import { GlobalStyle } from './styles/global-styles';
import Routes from './router';
import { useDispatch } from 'react-redux';
import Session from './store/actions/session.actions';
import api from './services/api';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { notification } from 'antd';
import 'antd/dist/antd.css';
import './styles/antd-loading-fix.css';
import Loading from './components/Loading';
import ErrorBoundary from './components/ErrorBoundary';
import { ThemeContextProvider, useThemeMode } from './contexts/ThemeContext';

notification.config({
  placement: 'bottomRight',
});
dayjs.locale('pt-br');

interface ThemedAppProps {
  children: React.ReactNode;
}

const ThemedApp: React.FC<ThemedAppProps> = ({ children }) => {
  const { currentTheme } = useThemeMode();

  return (
    <ThemeProvider theme={currentTheme}>
      <ConfigProvider locale={ptBR}>{children}</ConfigProvider>
    </ThemeProvider>
  );
};

function App() {
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();

  const sessionLogin = useCallback(
    (user: any) => dispatch(Session.Login(user)),
    [dispatch],
  );

  const unpackToken = useCallback(
    async (data: any, firstInit = false) => {
      if (!firstInit) {
        // Login: dados do user vêm direto da resposta do backend
        if (data && data.user) {
          await sessionLogin(data.user);
        }
        setLoading(false);
        return;
      }

      // Init: verificar sessão via cookie httpOnly chamando /session/me
      try {
        const response = await api.get('/session/me', {
          _skipErrorNotification: true,
        } as any);
        if (response.data.success && response.data.user) {
          await sessionLogin(response.data.user);
        }
      } catch (error) {
        // Cookie expirado ou inexistente - usuário não logado
      }

      setLoading(false);
    },
    [sessionLogin],
  );

  const appState = useMemo(
    () => ({
      loading,
      actions: {
        unpackToken,
      },
    }),
    [loading, unpackToken],
  );

  useEffect(() => {
    unpackToken(null, true);
  }, [unpackToken]);

  return (
    <ThemeContextProvider>
      <ThemedApp>
        <GlobalStyle />
        {loading ? (
          <Loading />
        ) : (
          <BrowserRouter>
            <ErrorBoundary>
              <Routes appState={appState} />
            </ErrorBoundary>
          </BrowserRouter>
        )}
      </ThemedApp>
    </ThemeContextProvider>
  );
}

export default App;
